import { cx } from '@emotion/css';
import { getDesignSystemIcon } from '@phoenix/design-system/frontend/util/path.utils';
import { AnimatePresence, HTMLMotionProps, motion } from 'framer-motion';
import { Children, cloneElement, forwardRef, isValidElement, ReactElement, useState } from 'react';
import styles from './fab-button.module.scss';
import { FabMenu, FabMenuProps } from './fab-menu';

const plusIcon = getDesignSystemIcon('icon-plus-16.svg');

export interface FabButtonProps extends HTMLMotionProps<'button'> {
  btnType: 'white' | 'orange' | 'grey';
  icon: string;
  href?: string;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  children?: ReactElement;
  className?: string;
}

export const FabButton = forwardRef<HTMLButtonElement, React.PropsWithChildren<FabButtonProps>>(
  ({ children, btnType, icon, size = 'medium', disabled = false, className, ...otherProps }, ref) => {
    const typeClassNames: string[] = [];

    const [isOpen, setIsOpen] = useState(false);

    switch (btnType) {
      case 'white':
        typeClassNames.push(
          //default
          'bg-primary-white',
          'text-secondary-deep',
          // disabled
          'disabled:text-secondary-mid',
          'disabled:bg-primary-white',
          // hover
          'hover:bg-secondary-light',
          // focus
          'focus-visible:bg-secondary-light',
          // active / pressed
          'active:bg-secondary'
        );
        break;

      case 'orange':
        typeClassNames.push(
          //default
          'bg-primary-orange',
          'text-seconday-deep',
          // disabled
          'disabled:bg-secondary',
          // hover
          'hover:bg-primary-orange-dark',
          // focus
          'focus-visible:bg-primary-orange-dark',
          // active / pressed
          'active:bg-primary-orange-deep'
        );
        break;

      case 'grey':
        typeClassNames.push(
          //default
          'bg-primary-black',
          'text-primary-white',
          // disabled
          'disabled:bg-secondary',
          // hover
          'hover:bg-secondary-dark',
          // focus
          'focus-visible:bg-secondary-dark',
          // active / pressed
          'active:bg-secondary-dark'
        );
        break;
    }

    const hasMenu = children && children.type === FabMenu;

    const _onClick = !children
      ? undefined
      : () => {
          setIsOpen(!isOpen);
        };

    return (
      <div className={cx('relative', className)}>
        <AnimatePresence>
          {hasMenu && isOpen && (
            <motion.div
              animate={{ opacity: 1 }}
              initial={{ opacity: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className={cx('fixed bottom-0 left-0 right-0 top-0 z-30 h-screen w-screen  bg-primary-black/30')}
              onClick={() => setIsOpen(!isOpen)}
            />
          )}
        </AnimatePresence>
        <motion.div className="relative z-40">
          <motion.button
            disabled={disabled}
            className={cx(
              styles.shadow,
              typeClassNames,
              'mouse relative rounded-full transition duration-200 ease-in focus-visible:outline-primary-black outline-offset-[3px]',
              { small: 'h-9 w-9', medium: 'h-14 w-14', large: 'h-14 w-14' }[size]
            )}
            onClick={_onClick}
            ref={ref}
            {...otherProps}
          >
            <AnimatePresence>
              {isOpen ? (
                <motion.svg
                  className={cx(
                    'inline-block',
                    { small: 'h-4 w-4', medium: 'h-4 w-4', large: 'h-6 w-6 -translate-x-1/2 -translate-y-1/2' }[size]
                  )}
                  initial={{ opacity: 0, rotate: 0 }}
                  animate={{ opacity: 1, rotate: 45 }}
                  exit={{ opacity: 0, rotate: 0 }}
                  style={{ originX: '8px', originY: '8px' }}
                >
                  <use xlinkHref={`${plusIcon}#img`} />
                </motion.svg>
              ) : (
                <motion.svg
                  className={cx(
                    'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform',
                    { small: 'h-4 w-4', medium: 'h-4 w-4', large: 'h-6 w-6' }[size]
                  )}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, rotate: 45 }}
                >
                  <use xlinkHref={`${icon}#img`} />
                </motion.svg>
              )}
            </AnimatePresence>
          </motion.button>

          {Children.map(children, (child) => {
            if (isValidElement(child) && child.type === FabMenu) {
              return cloneElement(child, {
                open: isOpen,
                onClick: () => setIsOpen(!isOpen),
              } as Partial<FabMenuProps>);
            }
            throw new Error('Only <FabMenu /> components are inside a FabButton allowed');
          })}
        </motion.div>
      </div>
    );
  }
);
